import { MoreHorizontal } from "lucide-react"
import type { ActivityEvent, ActivityGroup, CommentContext, GroupRenderer } from "../../events/messageGroups"
import { compactInline, stringifyRaw } from "../../events/presentation"

function eventCount(event: ActivityEvent): number {
    return event.count ?? 1
}

function totalCount(group: ActivityGroup): number {
    return group.events.reduce((sum, event) => sum + eventCount(event), 0)
}

function truncatedRaw(value: unknown): string {
    const raw = stringifyRaw(value)
    const maxLength = 12000
    if (raw.length <= maxLength) return raw
    return `${raw.slice(0, maxLength).trim()}\n... truncated ${raw.length - maxLength} characters`
}

export function ActivityEventsContent({ events }: { events: ActivityEvent[] }) {
    return (
        <div className="divide-y divide-border">
            {events.map((event, index) => {
                const count = eventCount(event)
                const label = count > 1 ? `${event.label} x${count}` : event.label
                return (
                    <details key={`${event.messageIndex}-${event.originalType ?? event.label}-${index}`} className="group">
                        <summary className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs hover:bg-base-200">
                            <span className="font-medium text-base-content">{label}</span>
                            {event.summary && <span className="truncate text-muted">{compactInline(event.summary, 120)}</span>}
                            <span className="ml-auto font-mono text-muted">{event.originalType}</span>
                        </summary>
                        <pre className="max-h-96 overflow-auto whitespace-pre-wrap border-t border-border bg-base-200/50 px-3 py-2 text-xs">
                            {truncatedRaw(event.raw)}
                        </pre>
                    </details>
                )
            })}
        </div>
    )
}

export const activityRenderer: GroupRenderer<ActivityGroup> = {
    getLabel: (group) => {
        const count = totalCount(group)
        return `${count} more event${count === 1 ? "" : "s"}`
    },
    getIcon: () => <MoreHorizontal size="0.85em" className="text-muted flex-shrink-0" />,
    getStatusIcon: () => null,
    getHeaderInfo: () => null,
    renderContent: (group, _ctx: CommentContext) => <ActivityEventsContent events={group.events} />,
}
